/**
 * Paletas 3D por región. Cada una tiene su ventana HU (WW/WC) para cuantizar
 * el volumen Uint8 y una LUT con colores de tejido.
 * t = (HU − (WC − WW/2)) / WW  (clamp 0…1)
 */

export const MPR3D_VOLUME_WW = 2000
export const MPR3D_VOLUME_WC = 0

export const MPR3D_REGION_PALETTES = [
  { id: 'general', label: 'General', ww: 2000, wc: 0 },
  { id: 'hueso', label: 'Hueso', ww: 1800, wc: 400 },
  { id: 'torax', label: 'Tórax', ww: 1600, wc: -400 },
  { id: 'abdomen', label: 'Abdomen', ww: 400, wc: 40 },
  { id: 'angio', label: 'Vasos', ww: 700, wc: 200 },
  { id: 'craneo', label: 'Cráneo', ww: 2200, wc: 500 },
  // Ventana amplia: parénquima + blando + vasos + hueso (el W80 puro aplastaba el hueso a blanco).
  { id: 'cerebro', label: 'Cerebro', ww: 900, wc: 200 },
]

/** @deprecated capas sueltas; las paletas de región las reemplazan. */
export const MPR3D_TISSUE_LAYERS = [
  { id: 'hueso', label: 'Hueso' },
  { id: 'blando', label: 'Tejido' },
  { id: 'parenquima', label: 'Parénquima' },
]

export const MPR3D_TISSUE_PRESETS = [
  { id: 'compuesto', label: 'General' },
  ...MPR3D_TISSUE_LAYERS,
]

function lut512(sample) {
  const n = 512
  const px = new Uint8Array(n * 4)
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1)
    const c = sample(t)
    const o = i * 4
    px[o] = c.r | 0
    px[o + 1] = c.g | 0
    px[o + 2] = c.b | 0
    px[o + 3] = Math.max(0, Math.min(255, Math.round((c.a || 0) * 255)))
  }
  return px
}

function rgba(r, g, b, a) {
  return { r, g, b, a }
}

function lerpC(a, b, k) {
  return rgba(
    a.r + (b.r - a.r) * k,
    a.g + (b.g - a.g) * k,
    a.b + (b.b - a.b) * k,
    a.a + (b.a - a.a) * k
  )
}

function fromStops(stops) {
  const list = stops.slice().sort((a, b) => a.t - b.t)
  return (t) => {
    if (t <= list[0].t) return rgba(list[0].r, list[0].g, list[0].b, list[0].a)
    for (let i = 1; i < list.length; i++) {
      if (t <= list[i].t) {
        const k = (t - list[i - 1].t) / Math.max(1e-6, list[i].t - list[i - 1].t)
        return lerpC(list[i - 1], list[i], k)
      }
    }
    const s = list[list.length - 1]
    return rgba(s.r, s.g, s.b, s.a)
  }
}

function huToT(hu, ww, wc) {
  const lo = wc - ww / 2
  if (!(ww > 0)) return 0
  const t = (hu - lo) / ww
  return t < 0 ? 0 : t > 1 ? 1 : t
}

function stop(hu, r, g, b, a, voi) {
  return { t: huToT(hu, voi.ww, voi.wc), r, g, b, a }
}

export function getPaletteVoi(id) {
  const p = MPR3D_REGION_PALETTES.find((x) => x.id === id)
  if (p && p.ww > 0) return { ww: p.ww, wc: p.wc }
  return { ww: MPR3D_VOLUME_WW, wc: MPR3D_VOLUME_WC }
}

function tfWith(id, build) {
  return build(getPaletteVoi(id))
}

function tfGeneral(voi) {
  // Una superficie de cuerpo (como Hueso): sin picos de alpha apilados.
  return fromStops([
    stop(-1000, 0, 0, 0, 0, voi),
    stop(-180, 0, 0, 0, 0, voi),
    stop(-70, 228, 186, 148, 0.32, voi),
    stop(50, 196, 128, 108, 0.58, voi),
    stop(180, 168, 88, 78, 0.18, voi),
    stop(380, 232, 218, 196, 0.42, voi),
    stop(900, 250, 246, 232, 0.82, voi),
  ])
}

function tfHueso(voi) {
  return fromStops([
    stop(-1000, 0, 0, 0, 0, voi),
    stop(180, 0, 0, 0, 0, voi),
    // Tonos más bajos para que el sombreado tenga rango (no “blanco plano”).
    stop(280, 168, 150, 128, 0.28, voi),
    stop(420, 198, 182, 158, 0.52, voi),
    stop(650, 228, 214, 190, 0.78, voi),
    stop(1100, 242, 232, 212, 0.92, voi),
  ])
}

function tfTorax(voi) {
  return fromStops([
    stop(-1000, 0, 0, 0, 0, voi),
    stop(-820, 0, 0, 0, 0, voi),
    stop(-650, 200, 110, 108, 0.38, voi),
    stop(-400, 186, 96, 92, 0.22, voi),
    stop(50, 168, 80, 74, 0.16, voi),
    stop(400, 236, 224, 200, 0.48, voi),
    stop(900, 250, 246, 230, 0.78, voi),
  ])
}

function tfAbdomen(voi) {
  return fromStops([
    stop(-1000, 0, 0, 0, 0, voi),
    stop(-120, 0, 0, 0, 0, voi),
    stop(-20, 210, 160, 90, 0.36, voi),
    stop(50, 176, 96, 70, 0.55, voi),
    stop(140, 150, 78, 62, 0.16, voi),
    stop(500, 240, 228, 208, 0.52, voi),
    stop(900, 250, 244, 226, 0.78, voi),
  ])
}

function tfAngio(voi) {
  return fromStops([
    stop(-1000, 0, 0, 0, 0, voi),
    stop(80, 0, 0, 0, 0, voi),
    stop(140, 140, 28, 32, 0.10, voi),
    stop(220, 210, 36, 36, 0.50, voi),
    stop(380, 236, 64, 48, 0.84, voi),
    stop(600, 244, 220, 200, 0.16, voi),
  ])
}

/** Calota y huesos de la cara; encéfalo casi oculto. */
function tfCraneo(voi) {
  return fromStops([
    stop(-1000, 0, 0, 0, 0, voi),
    stop(150, 0, 0, 0, 0, voi),
    // Marfil medio (no casi-blanco): relieve y órbitas se leen mejor.
    stop(240, 150, 132, 112, 0.26, voi),
    stop(360, 186, 168, 145, 0.48, voi),
    stop(520, 214, 198, 172, 0.68, voi),
    stop(780, 236, 224, 200, 0.82, voi),
    stop(1100, 245, 236, 214, 0.92, voi),
  ])
}

/**
 * Encéfalo + estructuras vecinas (W≈900 C≈200 → HU −250…+650).
 * Opacidad por tejido; color anatómico (gris DICOM se aplica luego con CLUT).
 */
function tfCerebro(voi) {
  return fromStops([
    stop(-250, 0, 0, 0, 0, voi),
    // LCR / ventrículos — rampas suaves (menos isosuperficies)
    stop(-5, 55, 70, 88, 0.02, voi),
    stop(8, 90, 105, 120, 0.05, voi),
    // Sustancia blanca
    stop(22, 198, 190, 175, 0.10, voi),
    stop(30, 208, 198, 182, 0.16, voi),
    // Sustancia gris
    stop(38, 155, 148, 142, 0.18, voi),
    stop(45, 140, 134, 130, 0.16, voi),
    // Tejido blando / músculo
    stop(55, 128, 105, 95, 0.07, voi),
    stop(70, 118, 92, 82, 0.05, voi),
    // Vascular / sangre
    stop(85, 175, 55, 52, 0.22, voi),
    stop(110, 150, 70, 65, 0.10, voi),
    // Hueco hacia hueso
    stop(160, 40, 40, 40, 0.01, voi),
    // Hueso trabecular
    stop(220, 210, 205, 195, 0.14, voi),
    stop(320, 235, 230, 220, 0.28, voi),
    // Cortical
    stop(500, 250, 248, 242, 0.52, voi),
    stop(650, 255, 255, 252, 0.70, voi),
  ])
}

/** Estilos de color (CLUT), independientes del preset de región — como OsiriX/Slicer. */
export const MPR3D_COLOR_STYLES = [
  { id: 'anatomico', label: 'Anatómico', swatch: '#c8b8a8' },
  { id: 'gris', label: 'Gris', swatch: '#b0b0b0' },
  { id: 'azul', label: 'Azul', swatch: '#6a90b8' },
  { id: 'ambar', label: 'Ámbar', swatch: '#d4a574' },
  { id: 'hueso', label: 'Marfil', swatch: '#efe6d6' },
  { id: 'verde', label: 'Verde', swatch: '#7aaa7a' },
  { id: 'fuego', label: 'Fuego', swatch: '#e07040' },
]

/** Gris tipo DICOM: la intensidad sigue el HU (t), no el color previo. */
function grayFromHu(t, a) {
  // Curva suave: LCR oscuro → parénquima medio → hueso casi blanco
  const g = Math.max(0, Math.min(255, Math.round(Math.pow(Math.max(0, t), 0.85) * 255)))
  return rgba(g, g, g, a)
}

function applyColorStyle(sample, styleId) {
  if (!styleId || styleId === 'anatomico') return sample
  return (t) => {
    const c = sample(t)
    if (!(c.a > 0.001)) return c
    if (styleId === 'gris') return grayFromHu(t, c.a)
    const lum = 0.299 * c.r + 0.587 * c.g + 0.114 * c.b
    // Mezcla HU + luminancia del TF para no perder bandas de tejido
    const huG = Math.pow(Math.max(0, t), 0.85) * 255
    const base = lum * 0.45 + huG * 0.55
    if (styleId === 'azul') {
      return rgba(base * 0.50 + 14, base * 0.68 + 24, Math.min(255, base * 0.92 + 42), c.a)
    }
    if (styleId === 'ambar') {
      return rgba(Math.min(255, base * 1.02 + 32), base * 0.78 + 24, base * 0.40 + 10, c.a)
    }
    if (styleId === 'hueso') {
      return rgba(Math.min(255, base * 0.92 + 44), Math.min(255, base * 0.90 + 36), base * 0.80 + 24, c.a)
    }
    if (styleId === 'verde') {
      return rgba(base * 0.42 + 14, Math.min(255, base * 0.95 + 32), base * 0.52 + 16, c.a)
    }
    if (styleId === 'fuego') {
      return rgba(Math.min(255, base * 0.80 + 68), base * 0.42 + 16, base * 0.20 + 6, c.a)
    }
    return c
  }
}

const REGION_TF = {
  general: () => tfWith('general', tfGeneral),
  hueso: () => tfWith('hueso', tfHueso),
  torax: () => tfWith('torax', tfTorax),
  abdomen: () => tfWith('abdomen', tfAbdomen),
  angio: () => tfWith('angio', tfAngio),
  craneo: () => tfWith('craneo', tfCraneo),
  cerebro: () => tfWith('cerebro', tfCerebro),
  compuesto: () => tfWith('general', tfGeneral),
  blando: () => tfWith('abdomen', tfAbdomen),
  parenquima: () => tfWith('torax', tfTorax),
}

/**
 * TF clínica: gris DICOM lineal sobre t del volumen (WW/WC actual).
 * No usa la curva de opacidad de la paleta regional (anclada a otro VOI).
 */
export function buildClinicalTf(colorStyle = 'anatomico') {
  const sample = (t) => {
    if (t < 0.035) return rgba(0, 0, 0, 0)
    const g = Math.max(0, Math.min(255, Math.round(Math.pow(t, 0.9) * 255)))
    const u = Math.max(0, (t - 0.05) / 0.95)
    const a = Math.min(0.38, 0.02 + Math.pow(u, 1.45) * 0.40)
    return rgba(g, g, g, a)
  }
  return lut512(applyColorStyle(sample, colorStyle || 'anatomico'))
}

export function buildRegionTf(id, colorStyle = 'anatomico') {
  const make = REGION_TF[id] || REGION_TF.general
  return lut512(applyColorStyle(make(), colorStyle))
}

export function buildPresetTf(id) {
  return buildRegionTf(id)
}

export function buildLayersTf(layers = {}) {
  const h = layers.hueso !== false
  const b = layers.blando !== false
  const p = layers.parenquima !== false
  if (h && b && p) return lut512(tfWith('general', tfGeneral))
  if (h && !b && !p) return lut512(tfWith('hueso', tfHueso))
  if (!h && b && !p) return lut512(tfWith('abdomen', tfAbdomen))
  if (!h && !b && p) return lut512(tfWith('torax', tfTorax))
  return lut512((t) => {
    const samples = []
    if (p) samples.push(tfWith('torax', tfTorax)(t))
    if (b) samples.push(tfWith('abdomen', tfAbdomen)(t))
    if (h) samples.push(tfWith('hueso', tfHueso)(t))
    if (!samples.length) return rgba(0, 0, 0, 0)
    let best = samples[0]
    for (let i = 1; i < samples.length; i++) {
      if (samples[i].a > best.a) best = samples[i]
    }
    return best
  })
}

export function mpr3dPresetLabel(id) {
  const p = MPR3D_REGION_PALETTES.find((x) => x.id === id)
    || MPR3D_TISSUE_PRESETS.find((x) => x.id === id)
  return (p && p.label) || 'General'
}

function dicomStr(image, tag) {
  try {
    if (!image || !image.data || typeof image.data.string !== 'function') return ''
    return String(image.data.string(tag) || '')
  } catch (e) {
    return ''
  }
}

function fold(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

function hasAny(text, keys) {
  for (let i = 0; i < keys.length; i++) {
    if (text.indexOf(keys[i]) >= 0) return true
  }
  return false
}

/**
 * Infiere paleta 3D desde metadatos DICOM (Body Part, estudio, serie, protocolo).
 */
export function inferMpr3dPalette(image, file) {
  const body = fold(dicomStr(image, 'x00180015'))
  const blob = fold([
    body,
    dicomStr(image, 'x00081030'),
    dicomStr(image, 'x0008103e'),
    dicomStr(image, 'x00181030'),
    dicomStr(image, 'x00321060'),
    dicomStr(image, 'x00081050'),
    dicomStr(image, 'x00080060'),
    file && file.seriesDescription,
    file && file.studyDescription,
    file && file.series && file.series.seriesDescription,
    file && file.study && file.study.studyDescription,
    file && file.series && file.series.protocolName,
    file && file.bodyPartExamined,
  ].filter(Boolean).join(' '))

  const scores = {
    angio: 0,
    cerebro: 0,
    craneo: 0,
    torax: 0,
    abdomen: 0,
    hueso: 0,
  }

  if (hasAny(blob, ['cta', 'angio', 'arterio', 'angiograf', 'vascular', 'aorta'])) scores.angio += 8
  if (hasAny(blob, ['cerebro', 'brain', 'encefal', 'encephalo', 'intracran', 'sylvian', 'fosa posterior'])) scores.cerebro += 8
  if (hasAny(blob, ['craneo', 'skull', 'calota', 'calvaria', 'facial', 'senos paranas'])) scores.craneo += 7
  if (hasAny(blob, ['torax', 'thorax', 'chest', 'pulmon', 'lung', 'pecho', 'mediast'])) scores.torax += 6
  if (hasAny(blob, ['abdomen', 'pelvis', 'abdpel', 'higado', 'liver', 'rinon', 'kidney', 'urograf'])) scores.abdomen += 6
  if (hasAny(blob, ['columna', 'spine', 'verteb', 'rodilla', 'knee', 'hombro', 'shoulder', 'extrem', 'cspine', 'lspine', 'tspine'])) scores.hueso += 5

  if (body === 'brain' || body === 'head') scores.cerebro += 5
  if (body === 'skull') scores.craneo += 6
  if (body === 'chest' || body === 'thorax' || body === 'lung') scores.torax += 6
  if (body === 'abdomen' || body === 'pelvis' || body === 'abdpelvis') scores.abdomen += 6
  if (body === 'cspine' || body === 'tspine' || body === 'lspine' || body === 'spine' || body === 'extremity') scores.hueso += 5
  if (hasAny(blob, ['cabeza', 'head', 'craneo']) && scores.cerebro < 3 && scores.craneo < 3) scores.cerebro += 3
  if (hasAny(blob, ['hueso', 'bone']) && scores.craneo < 4) scores.hueso += 2

  let best = 'general'
  let bestScore = 2
  Object.keys(scores).forEach((id) => {
    if (scores[id] > bestScore) {
      bestScore = scores[id]
      best = id
    }
  })
  return best
}

export function inferMpr3dPaletteFromFiles(files) {
  if (!files || !files.length) return 'general'
  const f0 = files.find((f) => f && f.image) || files[0]
  return inferMpr3dPalette(f0 && f0.image, f0)
}
